import { ComponentChildren, ComponentProps, Fragment, Ref } from 'preact';
import { LabelValueLayout } from '../UNSAFE_LabelValueLayout';
import { TextFieldContent } from './TextFieldContent';
import { StyledTextField } from './StyledTextField';
import { Size } from '../utils/UNSAFE_size';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';

// TextField takes the inputElem and userAssistanceContainer
// and renders the form component's root dom, along with the
// TextFieldContent, and the user assistance container.

// The PickedProps are the ones we pass through to the the TextFieldContent
type PickedPropsFromTextFieldContent = Pick<
  ComponentProps<typeof TextFieldContent>,
  | 'contentVariant'
  | 'startContent'
  | 'statusVariant'
  | 'styleVariant'
  | 'mainContent'
  | 'endContent'
  | 'resize'
>;
type PickedPropsFromHTMLElement = Pick<HTMLAttributesSignalExcluded<HTMLElement>, 'id'>;
// TODO: How should a parent component listen to events for the text field?  The recommendation
// was to remove event listeners from TextField itself and instead configure the TextFieldInput
// with them, for example, thus keeping TextField a more pure layout component.  The
// TextFieldInput renders the focusable input element, and may be a more natural place for
// listeners.  We would potentially have to add similar listeners for the ReadonlyTextField, but
// that component renders the focusable element itself;  there is no TextFieldInput or analogous
// component passed in.  (from review on 3/8/22)
type FocusEvents = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'onFocus' | 'onBlur'>;
type KeyEvents = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'onKeyDown'>;
type MouseEvents = Pick<
  HTMLAttributesSignalExcluded<HTMLDivElement>,
  'onMouseDown' | 'onMouseEnter' | 'onMouseLeave'
>;
type LabelProps = {
  label?: ComponentChildren;
  labelEdge?: 'inside' | 'start' | 'top';
  labelStartWidth?: Size;
};
type Props = PickedPropsFromTextFieldContent &
  PickedPropsFromHTMLElement &
  FocusEvents &
  KeyEvents &
  MouseEvents &
  LabelProps & {
    inlineUserAssistance?: ComponentChildren;
    mainFieldRef?: Ref<HTMLDivElement>;
  };

// TODO: Do we need named props for start/endContent, or could they just be children that get
// wrapped in a flex layout?  (from review on 3/8/22)

// TODO: Instead of passing the inputElem, label, and userAssistance as named props here, should we
// have a separate layout component that lays them out, which could just be passed as a child
// to the TextField?  (from review on 3/8/22)

export const TextField = ({
  contentVariant,
  id,
  endContent,
  mainContent,
  startContent,
  inlineUserAssistance,
  label,
  labelEdge,
  labelStartWidth,
  mainFieldRef,
  resize,
  statusVariant,
  styleVariant,
  onFocus,
  onBlur,
  onKeyDown,
  onMouseDown,
  onMouseEnter,
  onMouseLeave
}: Props) => {
  // TODO: What are the implications for interoperability between new preact-based form components
  // and the existing JET oj-form-layout?  The preact form components create their own start/top
  // labels, while oj-form-layout expects to create those itself and wire them to the existing form
  // components through attributes like labelled-by.  (from review on 3/8/22)

  // TODO: How should a parent form component render component-specific content within TextField,
  // for example an aria live region?  Instead of a children prop on TextField, could the parent
  // component pass comp-specific content through other props, like inputElem?   The parent could
  // have its own component that composes inputElem with additional DOM.  (from review on 3/8/22)

  const styledTextFieldChildren =
    label !== undefined && (labelEdge === 'start' || labelEdge === 'top') ? (
      <LabelValueLayout label={label} labelEdge={labelEdge} labelStartWidth={labelStartWidth}>
        <Fragment>
          <TextFieldContent
            contentVariant={contentVariant}
            endContent={endContent}
            mainContent={mainContent}
            resize={resize}
            rootRef={mainFieldRef}
            startContent={startContent}
            statusVariant={statusVariant}
            styleVariant={styleVariant}
          />
          {inlineUserAssistance}
        </Fragment>
      </LabelValueLayout>
    ) : (
      <Fragment>
        <TextFieldContent
          contentVariant={contentVariant}
          endContent={endContent}
          insideLabel={label}
          mainContent={mainContent}
          resize={resize}
          rootRef={mainFieldRef}
          startContent={startContent}
          statusVariant={statusVariant}
          styleVariant={styleVariant}
        />
        {inlineUserAssistance}
      </Fragment>
    );
  return (
    <StyledTextField
      id={id}
      hasInsideLabel={label !== undefined && labelEdge === 'inside'}
      onfocusin={onFocus}
      onfocusout={onBlur}
      onKeyDown={onKeyDown}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      variant={styleVariant}>
      {styledTextFieldChildren}
    </StyledTextField>
  );
};
